import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useDispatchEvents } from '@/hooks/useUplog';
import type { DispatchLogEntry } from '@/api/uplog.api';

const MONTH_NAMES = [
  '',
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  sent: {
    label: 'Enviado',
    className: 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400',
  },
  delivered: {
    label: 'Entregue',
    className: 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400',
  },
  read: {
    label: 'Lido',
    className: 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400',
  },
  failed: {
    label: 'Falhou',
    className: 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-400',
  },
  test: {
    label: 'Teste',
    className: 'border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400',
  },
};

const EVENT_LABEL: Record<string, string> = {
  SENT: 'Enviado para Zenvia',
  FAILED: 'Falha no envio',
  STATUS_SENT: 'Confirmado como enviado',
  STATUS_DELIVERED: 'Entregue ao destinatário',
  STATUS_READ: 'Lido pelo destinatário',
  STATUS_FAILED: 'Falha na entrega',
  STATUS_NOT_DELIVERED: 'Não entregue',
  STATUS_REJECTED: 'Rejeitado',
};

function fmtDate(v: string | Date | null) {
  if (!v) return '—';
  return format(new Date(v), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
}

interface Props {
  entry: DispatchLogEntry | null;
  onOpenChange: (open: boolean) => void;
}

export function UplogDispatchDetailDialog({ entry: e, onOpenChange }: Props) {
  const { data: events = [] } = useDispatchEvents(e?.id ?? null);
  const statusCfg = e ? (STATUS_CONFIG[e.status] ?? { label: e.status, className: '' }) : null;

  return (
    <Dialog open={!!e} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Detalhes do disparo</DialogTitle>
        </DialogHeader>

        {e && statusCfg && (
          <div className="space-y-4 overflow-y-auto pr-1">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-sm">{e.phone}</p>
                <p className="text-xs text-muted-foreground">
                  {e.documento} — {MONTH_NAMES[e.mes]} {e.ano}
                </p>
              </div>
              <Badge variant="outline" className={`shrink-0 ${statusCfg.className}`}>
                {statusCfg.label}
              </Badge>
            </div>

            {/* Timestamps */}
            <div className="overflow-hidden rounded-md border text-xs">
              <div className="flex justify-between border-b px-3 py-2">
                <span className="text-muted-foreground">Disparado em</span>
                <span className="font-mono">{fmtDate(e.dispatchedAt)}</span>
              </div>
              <div className="flex justify-between items-start gap-3 px-3 py-2">
                <span className="text-muted-foreground shrink-0">URL do Boleto</span>
                <a
                  href={e.boletoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-mono text-primary hover:underline text-right break-all"
                >
                  {e.boletoUrl}
                  <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                </a>
              </div>
            </div>

            {/* Error */}
            {e.errorMessage && (
              <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
                {e.errorMessage}
              </div>
            )}

            {/* Zenvia ID */}
            {e.zenviaMessageId && (
              <p className="text-xs text-muted-foreground">
                Zenvia ID: <span className="font-mono">{e.zenviaMessageId}</span>
              </p>
            )}

            {/* Payload sent to Zenvia */}
            {(() => {
              const sentEvent = events.find(
                (ev) => ev.eventType === 'SENT' || ev.eventType === 'FAILED',
              );
              const fields = sentEvent?.payload?.fields as Record<string, string> | undefined;
              if (!fields || Object.keys(fields).length === 0) return null;
              return (
                <div>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">Campos enviados</p>
                  <div className="overflow-hidden rounded-md border text-xs">
                    {Object.entries(fields).map(([k, v]) => (
                      <div
                        key={k}
                        className="flex flex-col gap-0.5 border-b px-3 py-2 last:border-0 sm:flex-row sm:justify-between sm:gap-3"
                      >
                        <span className="font-mono text-muted-foreground shrink-0">{`{{${k}}}`}</span>
                        <span className="font-mono break-all text-right">
                          {v ? (
                            v.startsWith('http') ? (
                              <a
                                href={v}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                              >
                                {v}
                              </a>
                            ) : (
                              v
                            )
                          ) : (
                            <span className="italic text-muted-foreground">vazio</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Event timeline */}
            {events.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Histórico de eventos
                </p>
                <ol className="relative space-y-3 border-l border-border pl-4">
                  {events.map((ev) => {
                    const p = ev.payload;
                    const detail = (p?.description ?? p?.error ?? null) as string | null;
                    const isFailure =
                      ev.eventType.includes('FAILED') ||
                      ev.eventType.includes('REJECTED') ||
                      ev.eventType.includes('NOT_DELIVERED');

                    return (
                      <li key={ev.id} className="relative">
                        <span
                          className={`absolute -left-[17px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${isFailure ? 'bg-destructive' : 'bg-primary'}`}
                        />
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className={`text-xs font-medium ${isFailure ? 'text-destructive' : ''}`}
                          >
                            {EVENT_LABEL[ev.eventType] ?? ev.eventType}
                          </span>
                          <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                            {format(new Date(ev.createdAt), 'dd/MM HH:mm:ss', { locale: ptBR })}
                          </span>
                        </div>
                        {detail && (
                          <p
                            className={`mt-0.5 text-[11px] ${isFailure ? 'text-destructive/80' : 'text-muted-foreground'}`}
                          >
                            {String(detail)}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
